<template>
  <div class="work-power-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>W = Fs cosθ | P = W/t$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="100%" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet">
          <!-- Floor -->
          <line x1="50" y1="350" x2="750" y2="350" stroke="#333" stroke-width="4" stroke-linecap="round" />
          
          <!-- Grid -->
          <defs>
            <pattern id="grid-wp" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,255,255,0.02)" stroke-width="0.5"/>
            </pattern>
          </defs>
          <rect width="800" height="400" fill="url(#grid-wp)" />

          <!-- Object -->
          <g :transform="`translate(${pos.x * 2}, 310)`">
            <rect x="-40" y="0" width="80" height="40" fill="#00d2ff" rx="4" />
            
            <!-- Label F -->
            <g :transform="`rotate(${-angle})`">
               <line x1="0" y1="20" x2="100" y2="20" stroke="#ff4757" stroke-width="6" marker-end="url(#arr-f-pro)" />
               <text x="110" y="25" fill="#ff4757" font-size="16" font-weight="bold">F</text>
            </g>

            <!-- Angle Label -->
            <path :d="arcPath" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="2" />
            <text x="40" y="0" fill="#aaa" font-size="12">θ={{ angle }}°</text>
          </g>

          <!-- Energy Stats Inside SVG -->
          <g transform="translate(50, 50)">
             <rect width="250" height="100" rx="8" fill="rgba(0,0,0,0.5)" stroke="rgba(255,255,255,0.1)" />
             <text x="20" y="35" fill="#2ed573" font-size="14" font-weight="bold">累计做功 W: {{ Math.abs(totalWork).toFixed(1) }} J</text>
             <rect x="20" y="55" width="210" height="10" fill="#222" rx="5" />
             <rect x="20" y="55" :width="Math.min(210, (Math.abs(totalWork) / 500) * 210)" height="10" fill="#2ed573" rx="5" />
             <text x="20" y="85" fill="#aaa" font-size="12">实时功率 P: {{ power.toFixed(1) }} W</text>
          </g>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>性质</label>
              <div class="value" :style="{ color: workTypeColor }">{{ workTypeText }}</div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center gap-6">
            <div class="flex-1">
               <label>力 F 与位移 s 的夹角 θ (Degrees)</label>
               <el-slider v-model="angle" :min="0" :max="180" size="small"></el-slider>
            </div>
            <div class="flex gap-4">
               <el-button type="primary" @click="start">{{ isMoving ? '重置' : '开始运动' }}</el-button>
               <el-button @click="reset">复位</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <svg height="0" width="0">
      <defs>
        <marker id="arr-f-pro" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
           <path d="M0,0 L6,3 L0,6 Z" fill="#ff4757" />
        </marker>
      </defs>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'WorkPowerViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 30,
      pos: { x: 50, y: 0 },
      forceMag: 50,
      totalWork: 0,
      isMoving: false,
      startTime: 0,
      power: 0,
      running: true,
      timer: null
    }
  },
  computed: {
    arcPath() {
      const r = 40;
      const rad = -this.angle * Math.PI / 180;
      const x = Math.cos(rad) * r;
      const y = Math.sin(rad) * r + 20;
      return `M ${r} 20 A ${r} ${r} 0 0 0 ${x} ${y}`;
    },
    workTypeText() {
      if (this.angle === 90) return '不做功';
      return this.angle < 90 ? '正功' : '负功';
    },
    workTypeColor() {
      if (this.angle === 90) return '#888';
      return this.angle < 90 ? '#2ed573' : '#ff4757';
    }
  },
  methods: {
    start() {
       if (this.isMoving) {
         this.reset();
         return;
       }
       this.reset();
       this.isMoving = true;
       this.startTime = Date.now();
       this.animate();
    },
    reset() {
       this.pos.x = 50;
       this.totalWork = 0;
       this.isMoving = false;
       this.power = 0;
       cancelAnimationFrame(this.timer);
    },
    animate() {
      if (!this.isMoving || !this.running) return;
      
      const speed = 3;
      this.pos.x += speed;
      
      const workInc = this.forceMag * speed * Math.cos(this.angle * Math.PI / 180);
      this.totalWork += workInc / 10;

      const duration = (Date.now() - this.startTime) / 1000;
      this.power = Math.abs(this.totalWork) / (duration || 0.1);

      if (this.pos.x > 350) {
        this.isMoving = false;
      } else {
        this.timer = requestAnimationFrame(this.animate);
      }
    }
  },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.work-power-viz {
  flex: 1; display: flex; flex-direction: column; gap: 0; height: 100%; width: 100%;
}
.canvas-box { flex: 1; background: #000; position: relative; }
.bottom-controls { padding: 20px 30px; background: #000; }
</style>
